"use client";

import React, { createContext, useContext, ReactNode } from "react";

/**
 * MistakeAnalysisContext provides mistake data for persistent highlighting in score-check.
 * This is separate from HighlightContext (hover-based) to enable dual-layer highlighting:
 * - Layer 1 (Persistent): Red text color for all mistakes (always visible)
 * - Layer 2 (Hover): Yellow background + underline (temporary, on hover)
 * 
 * Only score-check layout provides this context → zero impact on builder/dashboard pages.
 */

// ============================================================================
// Section-Specific Mistake Interfaces
// ============================================================================

/**
 * Word-level mistakes that should be highlighted individually (red text)
 */
interface WordMistakes {
  /** Incorrect spelling words: ["incorect", "wrods"] */
  spellingMistakes: string[];
  /** Incorrect grammar words/phrases: ["your wrong", "they is"] */
  grammarMistakes: string[];
  /** Buzzwords to avoid: ["synergy", "leverage"] */
  buzzWords: string[];
  /** Repeated action verbs: ["managed", "developed"] */
  repeatedWords: string[];
  /** Personal pronouns: ["I", "my", "me"] */
  personalPronouns: string[];
  /** Non-past tense action verbs: ["manage", "develop"] */
  actionVerbIssues: string[];
}

/**
 * Sentence-level mistakes that should be highlighted as full sentences
 */
interface SentenceMistakes {
  /** Full sentences longer than 25 words */
  longSentences: string[];
  /** Full statements not ending with a period */
  doesNotEndsWithPeriod: string[];
  /** Repeated phrases (trigram-based): ["responsible for developing"] */
  repeatedPhrases: string[];
}

/**
 * Complete mistake data for a single section item (e.g., Experience #1, Project #2)
 */
interface SectionItemMistakes {
  /** Word-level mistakes for individual highlighting */
  words: WordMistakes;
  /** Sentence-level mistakes for full-sentence highlighting */
  sentences: SentenceMistakes;
  /** Whether this item has passive voice issues (affects entire section) */
  hasPassiveVoice: boolean;
}

/**
 * Mistakes organized by section type and item index
 * Example: experience[0] = mistakes for first work experience
 */
interface SectionMistakesMap {
  [itemIndex: number]: SectionItemMistakes;
}

// ============================================================================
// Overall/Global Mistakes (Across All Sections)
// ============================================================================

/**
 * Global mistakes that span across the entire resume
 */
interface OverallMistakes {
  /** Repeated action verbs across all sections: ["managed", "developed"] */
  repeatedActionVerbs: string[];
  /** Repeated action verbs with counts: [{verb: "managed", count: 3}] */
  repeatedActionVerbsWithCounts: Array<{ verb: string; count: number }>;
  /** Repeated sentences across all sections (full sentences) */
  repeatedSentences: string[];
}

// ============================================================================
// Complete Mistake Analysis Context
// ============================================================================

/**
 * Complete mistake data structure for the entire resume
 * Organized by section type (profile, experience, education, projects, skills, overall)
 */
interface MistakeAnalysisData {
  /** Personal/Profile section mistakes (single item, no index) */
  profile: SectionItemMistakes | null;
  /** Work experience section mistakes (indexed by experience number) */
  experience: SectionMistakesMap;
  /** Education section mistakes (indexed by education number) */
  education: SectionMistakesMap;
  /** Projects section mistakes (indexed by project number) */
  projects: SectionMistakesMap;
  /** Skills section mistakes (single item, no index) */
  skills: SectionItemMistakes | null;
  /** Overall/global mistakes across entire resume */
  overall: OverallMistakes;
}

/**
 * Context value type
 */
interface MistakeAnalysisContextValue {
  /** The complete mistake data for the resume, or null if not in score-check */
  mistakeData: MistakeAnalysisData | null;
  /** Whether mistake analysis is enabled (true only in score-check) */
  enabled: boolean;
}

// ============================================================================
// Context Creation
// ============================================================================

const MistakeAnalysisContext = createContext<MistakeAnalysisContextValue>({
  mistakeData: null,
  enabled: false,
});

// ============================================================================
// Provider Component
// ============================================================================

interface MistakeAnalysisProviderProps {
  children: ReactNode;
  /** Mistake data transformed from ResumeAnalysis - null when not in score-check */
  mistakeData: MistakeAnalysisData | null;
}

export const MistakeAnalysisProvider: React.FC<
  MistakeAnalysisProviderProps
> = ({ children, mistakeData }) => {
  return (
    <MistakeAnalysisContext.Provider
      value={{
        mistakeData,
        enabled: mistakeData !== null,
      }}
    >
      {children}
    </MistakeAnalysisContext.Provider>
  );
};

// ============================================================================
// Hook for Consuming Context
// ============================================================================

/**
 * Hook to access mistake analysis data in resume components
 * Returns null mistakeData when not in score-check (builder/dashboard pages)
 * 
 * @example
 * const { mistakeData, enabled } = useMistakeAnalysis();
 * if (enabled && mistakeData) {
 *   // Apply persistent red highlighting
 * }
 */
export const useMistakeAnalysis = (): MistakeAnalysisContextValue => {
  return useContext(MistakeAnalysisContext);
};

// ============================================================================
// Helper Functions for Mistake Checking
// ============================================================================

/**
 * Check if a text string matches any word-level mistake
 * @param text - The text to check
 * @param section - Section type (profile, experience, education, projects, skills, overall)
 * @param itemIndex - Item index within section (null for profile/skills/overall)
 * @param mistakeData - The complete mistake data
 * @returns true if text matches any word mistake
 */
export const isWordMistake = (
  text: string,
  section: string,
  itemIndex: number | null,
  mistakeData: MistakeAnalysisData | null
): boolean => {
  if (!mistakeData) {
    return false;
  }

  const normalizedText = text.trim().toLowerCase();
  let sectionData: SectionItemMistakes | null = null;

  // Get section-specific data
  if (section === "profile" || section === "skills") {
    sectionData = mistakeData[section as "profile" | "skills"];
  } else if (
    section === "experience" ||
    section === "education" ||
    section === "projects"
  ) {
    if (itemIndex !== null) {
      sectionData =
        mistakeData[section as "experience" | "education" | "projects"][
          itemIndex
        ];
    }
  } else if (section === "overall") {
    // Overall section uses different structure
    const overall = mistakeData.overall;
    // Split text into words and check each one
    const textWords = normalizedText.split(/\s+/).filter(w => w.length > 0);
    return textWords.some(word => 
      overall.repeatedActionVerbs.some((verb) => verb.toLowerCase() === word) ||
      overall.repeatedActionVerbsWithCounts.some((item) => item.verb.toLowerCase() === word)
    );
  }

  if (!sectionData) {
    return false;
  }

  const { words } = sectionData;
  
  // Split the text into individual words and check if ANY word is a mistake
  const textWords = normalizedText.split(/\s+/).filter(w => w.length > 0);
  
  const hasMistake = textWords.some(word => 
    words.spellingMistakes.some((m) => m.toLowerCase() === word) ||
    words.grammarMistakes.some((m) => m.toLowerCase() === word) ||
    words.buzzWords.some((m) => m.toLowerCase() === word) ||
    words.repeatedWords.some((m) => m.toLowerCase() === word) ||
    words.personalPronouns.some((m) => m.toLowerCase() === word) ||
    words.actionVerbIssues.some((m) => m.toLowerCase() === word)
  );

  return hasMistake;
};

/**
 * Check if a sentence matches any sentence-level mistake
 * @param sentence - The full sentence to check
 * @param section - Section type
 * @param itemIndex - Item index within section
 * @param mistakeData - The complete mistake data
 * @returns true if sentence matches any sentence mistake
 */
export const isSentenceMistake = (
  sentence: string,
  section: string,
  itemIndex: number | null,
  mistakeData: MistakeAnalysisData | null
): boolean => {
  if (!mistakeData) return false;

  // Normalize: trim, lowercase, and normalize internal whitespace
  const normalizedSentence = sentence.trim().toLowerCase().replace(/\s+/g, ' ');
  let sectionData: SectionItemMistakes | null = null;

  // Get section-specific data
  if (section === "profile" || section === "skills") {
    sectionData = mistakeData[section as "profile" | "skills"];
  } else if (
    section === "experience" ||
    section === "education" ||
    section === "projects"
  ) {
    if (itemIndex !== null) {
      sectionData =
        mistakeData[section as "experience" | "education" | "projects"][
          itemIndex
        ];
    }
  } else if (section === "overall") {
    // Check overall repeated sentences
    return mistakeData.overall.repeatedSentences.some(
      (s) => s.trim().toLowerCase().replace(/\s+/g, ' ') === normalizedSentence
    );
  }

  if (!sectionData) return false;

  const { sentences } = sectionData;

  // Helper function to normalize stored sentences
  const normalizeMistake = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

  // Check against all sentence-level mistake arrays
  return (
    sentences.longSentences.some(
      (s) => normalizeMistake(s) === normalizedSentence
    ) ||
    sentences.doesNotEndsWithPeriod.some(
      (s) => normalizeMistake(s) === normalizedSentence
    ) ||
    sentences.repeatedPhrases.some((s) => normalizeMistake(s) === normalizedSentence)
  );
};

/**
 * Check if a specific word (not sentence) is a mistake
 * This checks individual words only, not full sentences
 * @param word - A single word to check
 * @param section - Section type
 * @param itemIndex - Item index within section
 * @param mistakeData - The complete mistake data
 * @returns true if this specific word is a mistake
 */
export const isSingleWordMistake = (
  word: string,
  section: string,
  itemIndex: number | null,
  mistakeData: MistakeAnalysisData | null
): boolean => {
  if (!mistakeData || !word.trim()) {
    return false;
  }

  const normalizedWord = word.trim().toLowerCase();
  let sectionData: SectionItemMistakes | null = null;

  // Get section-specific data
  if (section === "profile" || section === "skills") {
    sectionData = mistakeData[section as "profile" | "skills"];
  } else if (
    section === "experience" ||
    section === "education" ||
    section === "projects"
  ) {
    if (itemIndex !== null) {
      sectionData =
        mistakeData[section as "experience" | "education" | "projects"][
          itemIndex
        ];
    }
  } else if (section === "overall") {
    // Overall section: check repeated action verbs
    return (
      mistakeData.overall.repeatedActionVerbs.some(
        (verb) => verb.toLowerCase() === normalizedWord
      ) ||
      mistakeData.overall.repeatedActionVerbsWithCounts.some(
        (item) => item.verb.toLowerCase() === normalizedWord
      )
    );
  }

  if (!sectionData) {
    return false;
  }

  const { words } = sectionData;

  // Check against all word-level mistake arrays (exact match only)
  return (
    words.spellingMistakes.some((m) => m.toLowerCase() === normalizedWord) ||
    words.grammarMistakes.some((m) => m.toLowerCase() === normalizedWord) ||
    words.buzzWords.some((m) => m.toLowerCase() === normalizedWord) ||
    words.repeatedWords.some((m) => m.toLowerCase() === normalizedWord) ||
    words.personalPronouns.some((m) => m.toLowerCase() === normalizedWord) ||
    words.actionVerbIssues.some((m) => m.toLowerCase() === normalizedWord)
  );
};

// /**
//  * Check if a text segment contains or matches a multi-word mistake phrase
//  * This is used for grammar/spelling mistakes that can be phrases, not just single words
//  * @param text - The text segment to check
//  * @param section - Section type
//  * @param itemIndex - Item index within section
//  * @param mistakeData - The complete mistake data
//  * @returns true if the text exactly matches a multi-word mistake phrase
//  */
// export const isPhraseMistake = (
//   text: string,
//   section: string,
//   itemIndex: number | null,
//   mistakeData: MistakeAnalysisData | null
// ): boolean => {
//   if (!mistakeData || !text.trim()) {
//     return false;
//   }

//   const normalizedText = text.trim().toLowerCase().replace(/\s+/g, ' ');
//   let sectionData: SectionItemMistakes | null = null;

//   // Get section-specific data
//   if (section === "profile" || section === "skills") {
//     sectionData = mistakeData[section as "profile" | "skills"];
//   } else if (
//     section === "experience" ||
//     section === "education" ||
//     section === "projects"
//   ) {
//     if (itemIndex !== null) {
//       sectionData =
//         mistakeData[section as "experience" | "education" | "projects"][
//           itemIndex
//         ];
//     }
//   }

//   if (!sectionData) {
//     return false;
//   }

//   const { words } = sectionData;

//   // Normalize and check if text matches any multi-word mistake phrase
//   const normalizeMistake = (m: string) => m.trim().toLowerCase().replace(/\s+/g, ' ');

//   // Check if this text is a multi-word phrase mistake (exact match)
//   return (
//     words.spellingMistakes.some((m) => m.includes(' ') && normalizeMistake(m) === normalizedText) ||
//     words.grammarMistakes.some((m) => m.includes(' ') && normalizeMistake(m) === normalizedText) ||
//     words.buzzWords.some((m) => m.includes(' ') && normalizeMistake(m) === normalizedText) ||
//     words.personalPronouns.some((m) => m.includes(' ') && normalizeMistake(m) === normalizedText)
//   );
// };

/**
 * Check if any part of a text contains a word mistake
 * Useful for checking if a phrase contains any mistake words
 */
export const containsWordMistake = (
  text: string,
  section: string,
  itemIndex: number | null,
  mistakeData: MistakeAnalysisData | null
): boolean => {
  if (!mistakeData) return false;

  // Split text into words and check each
  const words = text.split(/\s+/);
  return words.some((word) =>
    isWordMistake(word, section, itemIndex, mistakeData)
  );
};

// ============================================================================
// Type Exports
// ============================================================================

export type {
  MistakeAnalysisData,
  SectionItemMistakes,
  SectionMistakesMap,
  WordMistakes,
  SentenceMistakes,
  OverallMistakes,
  MistakeAnalysisContextValue,
};
